import { Pause, Play, SkipBack, SkipForward } from "lucide-react"
import type { ReactNode } from "react"

import { Button } from "@/components/ui/button"
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group"
import type { AnimationEvent } from "@/types"

import { FishEyeSeekBar } from "./fish-eye-seek-bar"
import type { PlaybackSpeed } from "./use-replay-reducer"

interface PlaybackControlsProps {
  isPlaying: boolean
  speed: PlaybackSpeed
  currentEventIndex: number
  totalEvents: number
  /** Full events array — passed to FishEyeSeekBar for turn grouping. */
  events: AnimationEvent[]
  homeTeamId: string
  awayTeamId: string
  homeTeamHue?: number
  awayTeamHue?: number
  onTogglePlay: () => void
  onStepForward: () => void
  onStepBack: () => void
  onSeek: (index: number) => void
  onSetSpeed: (speed: PlaybackSpeed) => void
  /** Optional slot rendered after the speed toggle (e.g. mobile narrative toggle button). */
  appendSlot?: ReactNode
}

/**
 * PlaybackControls
 *
 * Bottom bar with: step-back, play/pause, step-forward, fish-eye seek bar, speed toggle.
 */
export function PlaybackControls({
  isPlaying,
  speed,
  currentEventIndex,
  totalEvents,
  events,
  homeTeamId,
  awayTeamId,
  homeTeamHue,
  awayTeamHue,
  onTogglePlay,
  onStepForward,
  onStepBack,
  onSeek,
  onSetSpeed,
  appendSlot,
}: PlaybackControlsProps) {
  const atStart = currentEventIndex <= 0
  const atEnd = currentEventIndex >= totalEvents - 1

  return (
    <div
      className="flex shrink-0 items-center gap-4 px-5 py-3"
      style={{
        background: "oklch(0.14 0.005 230)",
        borderTop: "2px solid oklch(0.25 0.01 230)",
        boxShadow: "0 -2px 8px oklch(0 0 0 / 0.4)",
      }}
      data-testid="playback-controls"
    >
      {/* Step back */}
      <Button
        variant="ghost"
        size="icon"
        onClick={onStepBack}
        disabled={atStart}
        className="h-9 w-9 rounded-md transition-colors disabled:opacity-30"
        style={{
          color: "oklch(0.65 0.01 230)",
          background: "oklch(0.18 0.005 230 / 0.5)",
        }}
        aria-label="Step back"
        data-testid="step-back-button"
      >
        <SkipBack className="size-4" />
      </Button>

      {/* Play / Pause */}
      <Button
        variant="ghost"
        size="icon-lg"
        onClick={onTogglePlay}
        className="h-11 w-11 rounded-full shadow-lg transition-all active:scale-95"
        style={{
          background:
            "linear-gradient(180deg, oklch(0.56 0.24 28), oklch(0.44 0.20 28))",
          color: "oklch(0.95 0.02 80)",
          boxShadow:
            "inset 0 1px 0 oklch(0.65 0.20 28 / 0.4), 0 2px 4px oklch(0 0 0 / 0.4)",
        }}
        aria-label={isPlaying ? "Pause" : "Play"}
        data-testid="play-pause-button"
      >
        {isPlaying ? (
          <Pause className="size-5" fill="currentColor" strokeWidth={0} />
        ) : (
          <Play
            className="size-5 translate-x-0.5"
            fill="currentColor"
            strokeWidth={0}
          />
        )}
      </Button>

      {/* Step forward */}
      <Button
        variant="ghost"
        size="icon"
        onClick={onStepForward}
        disabled={atEnd}
        className="h-9 w-9 rounded-md transition-colors disabled:opacity-30"
        style={{
          color: "oklch(0.65 0.01 230)",
          background: "oklch(0.18 0.005 230 / 0.5)",
        }}
        aria-label="Step forward"
        data-testid="step-forward-button"
      >
        <SkipForward className="size-4" />
      </Button>

      {/* Fish-eye seek bar (includes event counter) */}
      <FishEyeSeekBar
        events={events}
        currentEventIndex={currentEventIndex}
        homeTeamId={homeTeamId}
        awayTeamId={awayTeamId}
        homeTeamHue={homeTeamHue}
        awayTeamHue={awayTeamHue}
        onSeek={onSeek}
      />

      {/* Speed toggle */}
      <ToggleGroup
        type="single"
        value={String(speed)}
        onValueChange={(val) => {
          if (val === "1" || val === "2" || val === "4") {
            onSetSpeed(Number(val) as PlaybackSpeed)
          }
        }}
        className="shrink-0 rounded-md"
        style={{ background: "oklch(0.10 0.005 230)" }}
        data-testid="speed-toggle"
      >
        {(["1", "2", "4"] as const).map((v) => (
          <ToggleGroupItem
            key={v}
            value={v}
            aria-label={`${v}x speed`}
            className="h-8 w-9 text-xs font-bold"
            style={
              String(speed) === v
                ? {
                    background: "oklch(0.28 0.02 230 / 0.8)",
                    color: "oklch(0.80 0.16 85)",
                    border: "1px solid oklch(0.6 0.12 85 / 0.5)",
                  }
                : {
                    color: "oklch(0.55 0.01 230)",
                  }
            }
          >
            {v}x
          </ToggleGroupItem>
        ))}
      </ToggleGroup>

      {appendSlot}
    </div>
  )
}
